import { jsx as _jsx } from "react/jsx-runtime";
import { setTokenGetter, setUnauthorizedHandler } from '@config/http';
import { authService } from '@services/auth.service';
import { isAppApiError } from '@utils/service.utils';
import { createContext, useCallback, useContext, useEffect, useState, } from 'react';
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null);
    // Update token getter whenever token changes
    useEffect(() => {
        setTokenGetter(() => token);
    }, [token]);
    // Set unauthorized handler (clears auth state on 401)
    useEffect(() => {
        setUnauthorizedHandler(() => {
            setIsLoading(false);
            setToken(null);
            setIsAuthenticated(false);
        });
    }, []);
    const authenticate = async (externalToken) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authService.authenticate(externalToken);
            // Update state
            setToken(response.token);
            setIsAuthenticated(true);
            setIsLoading(false);
            return true;
        }
        catch (err) {
            let errorMessage = 'Authentication failed. Please try again.';
            if (isAppApiError(err)) {
                errorMessage = err.message;
            }
            setError(errorMessage);
            setIsLoading(false);
            return false;
        }
    };
    const initAuthentication = useCallback(async (externalToken) => {
        if (!externalToken) {
            setIsLoading(false);
            return;
        }
        await authenticate(externalToken);
    }, []);
    return (_jsx(AuthContext.Provider, { value: {
            isAuthenticated,
            isLoading,
            token,
            error,
            authenticate,
            initAuthentication,
        }, children: children }));
};
// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
